import { TERRAIN_DEFS } from '../content/terrain';

/*
 * Atlas אריחי קרקע.
 *
 * הגרסה הקודמת ציירה `fillRect` בצבע שטוח לכל אריח, עם גיוון בהירות. זה עבד,
 * אבל נראה בדיוק כמו מה שהוא: רשת של ריבועים צבועים.
 *
 * כאן כל אריח מצויר מראש כמה פעמים עם מרקם פרוצדורלי, ובזמן ריצה הציור הוא
 * `drawImage` אחד לאריח — **בדיוק אותה עלות כמו fillRect**. המרקם בחינם.
 *
 * בנוסף נבנים אריחי מעבר: קצה של סוג קרקע שדוהה החוצה. אריח שגובל בסוג אחר מקבל
 * שכבה כזו, וכך הגבול בין דשא לחול או בין חול למים מיטשטש במקום להיות קו ישר.
 * **רק אריחי גבול משלמים על זה** — אריח בתוך שדה אחיד נשאר drawImage אחד.
 */

/** גודל תא ב-atlas. גדול מספיק כדי לא להיות מטושטש בזום המרבי (64px לאריח). */
const CELL = 64;

/** כמה וריאציות לכל סוג קרקע. יותר = פחות חזרתיות, יותר זיכרון. */
export const VARIANTS = 4;

export const SIDES = 4;

/**
 * סדר עדיפות למעברים: קרקע "חזקה" יותר גולשת על החלשה ממנה.
 * מים גולשים על חול (קו חוף), חול גולש על דשא, סלע גולש על הכול.
 */
const BLEND_PRIORITY: readonly number[] = TERRAIN_DEFS.map((def) =>
    def.id === 'grass' ? 0 : def.id === 'sand' ? 1 : def.id === 'rock' ? 2 : 3,
);

export function blendPriority(terrainId: number): number {
    return BLEND_PRIORITY[terrainId] ?? 0;
}

export interface TerrainAtlas {
    readonly canvas: HTMLCanvasElement;
    readonly cell: number;
    /** מקור האריח הבסיסי. */
    baseX(terrainId: number, variant: number): number;
    baseY(): number;
    /** מקור אריח המעבר: קצה של הסוג הזה שדוהה פנימה. */
    edgeX(terrainId: number, side: number): number;
    edgeY(): number;
    /** מקור אריח עפרות לפי דרגת עושר. */
    oreX(level: number): number;
    oreY(): number;
}

const ORE_LEVELS = 3;

export function buildTerrainAtlas(): TerrainAtlas {
    const columns = Math.max(TERRAIN_DEFS.length * VARIANTS, TERRAIN_DEFS.length * SIDES, ORE_LEVELS);
    const canvas = document.createElement('canvas');
    canvas.width = columns * CELL;
    canvas.height = 3 * CELL;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('לא ניתן ליצור הקשר קנבס ל-atlas הקרקע');

    // ---- שורה 0: אריחי בסיס
    TERRAIN_DEFS.forEach((def, terrainId) => {
        for (let variant = 0; variant < VARIANTS; variant++) {
            ctx.save();
            ctx.translate((terrainId * VARIANTS + variant) * CELL, 0);
            drawBase(ctx, def.id, def.color, terrainId * 977 + variant * 31);
            ctx.restore();
        }
    });

    // ---- שורה 1: אריחי מעבר
    TERRAIN_DEFS.forEach((def, terrainId) => {
        for (let side = 0; side < SIDES; side++) {
            ctx.save();
            ctx.translate((terrainId * SIDES + side) * CELL, CELL);
            drawEdge(ctx, def.color, side);
            ctx.restore();
        }
    });

    // ---- שורה 2: עפרות
    for (let level = 0; level < ORE_LEVELS; level++) {
        ctx.save();
        ctx.translate(level * CELL, 2 * CELL);
        drawOre(ctx, level);
        ctx.restore();
    }

    return {
        canvas,
        cell: CELL,
        baseX: (terrainId, variant) => (terrainId * VARIANTS + (variant % VARIANTS)) * CELL,
        baseY: () => 0,
        edgeX: (terrainId, side) => (terrainId * SIDES + side) * CELL,
        edgeY: () => CELL,
        oreX: (level) => Math.min(level, ORE_LEVELS - 1) * CELL,
        oreY: () => 2 * CELL,
    };
}

/*
 * גיבוב דטרמיניסטי לזרע הווריאציה. לא RNG של המשחק: הכול כאן תצוגה, והאריח
 * חייב להיראות אותו דבר בכל בנייה כדי שלא ירצד בין טעינות.
 */
function seeded(seed: number): () => number {
    let state = (seed * 2654435761) >>> 0;
    return () => {
        state = (Math.imul(state ^ (state >>> 15), 0x2c1b3c6d) + 0x9e3779b9) >>> 0;
        return ((state >>> 8) & 0xffff) / 0xffff;
    };
}

function drawBase(
    ctx: CanvasRenderingContext2D,
    kind: string,
    color: string,
    seed: number,
): void {
    const random = seeded(seed);

    ctx.fillStyle = color;
    ctx.fillRect(0, 0, CELL, CELL);

    if (kind === 'grass') {
        // כתמי צמחייה בגדלים שונים, ואז גבעולים דקים — מה שהופך שטח לשדה.
        for (let i = 0; i < 26; i++) {
            const x = random() * CELL;
            const y = random() * CELL;
            const r = 3 + random() * 7;
            ctx.fillStyle = random() > 0.5 ? 'rgba(70,96,48,0.45)' : 'rgba(28,40,20,0.45)';
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.strokeStyle = 'rgba(88,116,58,0.5)';
        ctx.lineWidth = 1.4;
        for (let i = 0; i < 14; i++) {
            const x = random() * CELL;
            const y = random() * CELL;
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + (random() - 0.5) * 5, y - 4 - random() * 4);
            ctx.stroke();
        }
    } else if (kind === 'sand') {
        for (let i = 0; i < 40; i++) {
            const x = random() * CELL;
            const y = random() * CELL;
            ctx.fillStyle = random() > 0.5 ? 'rgba(112,98,66,0.5)' : 'rgba(56,48,32,0.4)';
            ctx.fillRect(x, y, 1 + random() * 3, 1 + random() * 2);
        }
    } else if (kind === 'water') {
        // פסי גל אופקיים — מספיק כדי שמים ייקראו כמים ולא כמשטח כחול.
        for (let i = 0; i < 7; i++) {
            const y = random() * CELL;
            ctx.strokeStyle = `rgba(120,180,205,${0.05 + random() * 0.09})`;
            ctx.lineWidth = 1 + random() * 2;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.bezierCurveTo(CELL / 3, y - 3, (CELL * 2) / 3, y + 3, CELL, y);
            ctx.stroke();
        }
    } else if (kind === 'rock') {
        // פאות מוארות וכהות — נותנות תחושת נפח בלי מודל תלת-ממדי.
        for (let i = 0; i < 9; i++) {
            const x = random() * CELL;
            const y = random() * CELL;
            const w = 8 + random() * 20;
            const h = 8 + random() * 18;
            ctx.fillStyle = random() > 0.5 ? 'rgba(96,98,104,0.7)' : 'rgba(34,36,40,0.7)';
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + w, y + h * 0.3);
            ctx.lineTo(x + w * 0.6, y + h);
            ctx.closePath();
            ctx.fill();
        }
    }
}

/** קצה שדוהה פנימה. השכבה מצוירת על האריח השכן וכך הגבול מיטשטש. */
function drawEdge(ctx: CanvasRenderingContext2D, color: string, side: number): void {
    const stops: readonly [number, number, number, number][] = [
        [0, 0, 0, CELL], // 0 = מלמעלה
        [CELL, 0, 0, 0], // 1 = מימין
        [0, CELL, 0, 0], // 2 = מלמטה
        [0, 0, CELL, 0], // 3 = משמאל
    ];
    const [x0, y0, x1, y1] = stops[side] ?? stops[0]!;
    const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
    gradient.addColorStop(0, color);
    gradient.addColorStop(0.35, color);
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CELL, CELL);
}

/** עפרות: גבישים, לא כתם זהב. שלוש דרגות עושר. */
function drawOre(ctx: CanvasRenderingContext2D, level: number): void {
    const random = seeded(4242 + level * 77);
    const count = 6 + level * 7;

    for (let i = 0; i < count; i++) {
        const x = 6 + random() * (CELL - 12);
        const y = 6 + random() * (CELL - 12);
        const r = 3 + random() * (3 + level);

        ctx.fillStyle = 'rgba(20,14,4,0.55)';
        ctx.beginPath();
        ctx.ellipse(x + 1.5, y + 2, r * 1.05, r * 0.75, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = i % 3 === 0 ? '#f0c85a' : '#d9a53a';
        ctx.beginPath();
        ctx.moveTo(x, y - r);
        ctx.lineTo(x + r * 0.8, y);
        ctx.lineTo(x, y + r * 0.9);
        ctx.lineTo(x - r * 0.8, y);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = 'rgba(255,238,170,0.85)';
        ctx.beginPath();
        ctx.moveTo(x, y - r);
        ctx.lineTo(x + r * 0.35, y - r * 0.15);
        ctx.lineTo(x, y);
        ctx.closePath();
        ctx.fill();
    }
}
